import React, { useCallback, useContext, useMemo, useState } from 'react'

import { ErrorContext, ErrorMessage } from '@cash-web/shared-data-access-error'

export const ErrorProvider = ({ children }: React.PropsWithChildren) => {
  const [error, setError] = useState<ErrorMessage | undefined>(undefined)

  const clearError = useCallback(() => {
    if (error !== undefined) {
      setError(undefined)
    }
  }, [error, setError])

  const setErrorStrict = useCallback(
    (message: ErrorMessage) => {
      if (message.trim() === '') {
        throw new Error('setError message cannot be empty')
      }
      setError(message)
    },
    [setError]
  )

  const context = useMemo(() => ({ setError: setErrorStrict, clearError, error }), [setErrorStrict, clearError, error])

  return <ErrorContext.Provider value={context}>{children}</ErrorContext.Provider>
}

export type OnErrorProviderProps = {
  onError?: (message: ErrorMessage) => void
}

export const OnErrorProvider = ({ children, onError }: React.PropsWithChildren<OnErrorProviderProps>) => {
  const { setError, clearError, error } = useContext(ErrorContext)

  const setErrorWithOnError = useCallback(
    (message: ErrorMessage) => {
      setError(message)
      onError?.(message)
    },
    [setError, onError]
  )

  const context = useMemo(
    () => ({ setError: setErrorWithOnError, clearError, error }),
    [setErrorWithOnError, clearError, error]
  )

  return <ErrorContext.Provider value={context}>{children}</ErrorContext.Provider>
}
